import { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { PROCESSING_FEE } from '@constants/common';
import { PackageConfig } from '@hooks/common/useBilling';
import { usePricingContext } from '@pages/Pricing/context';
import styled, { css, keyframes } from 'styled-components';
import { CountUp } from 'use-count-up';

import Toggle from '@components/ToggleButtonGroup';

import { useTheme } from '@/components/theme-v2/ThemeContext';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const slideLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(0, 200, 224, 0.3), inset 0 0 20px rgba(0, 200, 224, 0.05);
  }
  50% {
    box-shadow: 0 0 30px rgba(0, 200, 224, 0.5), inset 0 0 30px rgba(0, 200, 224, 0.1);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Styled Components
const ModalOverlay = styled.div<{ $isDark?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => (props.$isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(15, 23, 42, 0.5)')};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: ${fadeIn} 0.2s ease-out;
  padding: 24px;
  box-sizing: border-box;
`;

const ModalContainer = styled.div<{ $isDark?: boolean }>`
  background: ${props =>
    props.$isDark
      ? 'linear-gradient(145deg, rgba(21, 28, 36, 0.98), rgba(15, 20, 25, 0.98))'
      : 'linear-gradient(145deg, #ffffff, #f8fafc)'};
  border: 1px solid ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.3)' : 'rgba(0, 163, 184, 0.3)')};
  border-radius: 16px;
  width: 100%;
  max-width: 800px;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  animation: ${slideUp} 0.3s ease-out;
  position: relative;
  margin: auto;
  box-shadow: ${props =>
    props.$isDark ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)' : '0 25px 50px -12px rgba(0, 0, 0, 0.15)'};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.6)' : 'rgba(0, 163, 184, 0.6)')},
      transparent
    );
  }

  &::after {
    content: '';
    position: absolute;
    top: -1px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 3px;
    background: ${props => (props.$isDark ? '#00c8e0' : '#00a3b8')};
    border-radius: 0 0 4px 4px;
    box-shadow: 0 0 20px ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.6)' : 'rgba(0, 163, 184, 0.4)')};
  }

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: ${props => (props.$isDark ? 'rgba(42, 53, 68, 0.3)' : 'rgba(226, 232, 240, 0.5)')};
  }
  &::-webkit-scrollbar-thumb {
    background: ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.4)' : 'rgba(0, 163, 184, 0.4)')};
    border-radius: 3px;
  }
`;

const ModalHeader = styled.div<{ $isDark?: boolean }>`
  padding: 28px 32px 20px;
  padding-right: 70px;
  border-bottom: 1px solid ${props => (props.$isDark ? 'rgba(42, 53, 68, 0.6)' : 'rgba(226, 232, 240, 0.8)')};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const ModalTitle = styled.h2<{ $isDark?: boolean }>`
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => (props.$isDark ? '#f0f4f8' : '#171717')};
  margin: 0;
  letter-spacing: -0.02em;

  span {
    color: ${props => (props.$isDark ? '#00c8e0' : '#00a3b8')};
  }
`;

const CurrentBalance = styled.div<{ $isDark?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.1)' : 'rgba(0, 163, 184, 0.1)')};
  border: 1px solid ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.2)' : 'rgba(0, 163, 184, 0.2)')};
  border-radius: 8px;

  span {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.875rem;
    color: ${props => (props.$isDark ? '#00c8e0' : '#00a3b8')};
    font-weight: 500;
  }
`;

const CloseButton = styled.button<{ $isDark?: boolean }>`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 36px;
  height: 36px;
  border: 1px solid ${props => (props.$isDark ? 'rgba(148, 163, 184, 0.3)' : 'rgba(148, 163, 184, 0.4)')};
  border-radius: 8px;
  background: ${props => (props.$isDark ? 'rgba(42, 53, 68, 0.4)' : 'rgba(241, 245, 249, 0.8)')};
  color: ${props => (props.$isDark ? '#94a3b8' : '#64748b')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    border-color: #dc2626;
    color: #dc2626;
    background: ${props => (props.$isDark ? 'rgba(255, 0, 51, 0.1)' : 'rgba(220, 38, 38, 0.1)')};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

// Stepper styles
const StepperContainer = styled.div<{ $isDark?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px 32px;
  border-bottom: 1px solid ${props => (props.$isDark ? 'rgba(42, 53, 68, 0.6)' : 'rgba(226, 232, 240, 0.8)')};
`;

const StepIndicator = styled.div<{ $active?: boolean; $completed?: boolean; $isDark?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StepCircle = styled.div<{ $active?: boolean; $completed?: boolean; $isDark?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.3s ease;

  ${props =>
    props.$completed
      ? css`
          background: ${props.$isDark ? '#00c8e0' : '#00a3b8'};
          color: ${props.$isDark ? '#0a0e14' : '#ffffff'};
          border: 2px solid ${props.$isDark ? '#00c8e0' : '#00a3b8'};
        `
      : props.$active
        ? css`
            background: ${props.$isDark ? 'rgba(0, 200, 224, 0.2)' : 'rgba(0, 163, 184, 0.15)'};
            color: ${props.$isDark ? '#00c8e0' : '#00a3b8'};
            border: 2px solid ${props.$isDark ? '#00c8e0' : '#00a3b8'};
          `
        : css`
            background: transparent;
            color: ${props.$isDark ? '#64748b' : '#94a3b8'};
            border: 2px solid ${props.$isDark ? 'rgba(100, 116, 139, 0.5)' : 'rgba(148, 163, 184, 0.5)'};
          `}
`;

const StepLabel = styled.span<{ $active?: boolean; $isDark?: boolean }>`
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => (props.$active ? (props.$isDark ? '#f0f4f8' : '#171717') : props.$isDark ? '#64748b' : '#94a3b8')};
`;

const StepConnector = styled.div<{ $completed?: boolean; $isDark?: boolean }>`
  width: 60px;
  height: 2px;
  background: ${props =>
    props.$completed
      ? props.$isDark
        ? '#00c8e0'
        : '#00a3b8'
      : props.$isDark
        ? 'rgba(100, 116, 139, 0.3)'
        : 'rgba(148, 163, 184, 0.3)'};
  transition: background 0.3s ease;
`;

const ModalBody = styled.div`
  padding: 24px 32px;
`;

const StepContent = styled.div`
  animation: ${slideLeft} 0.3s ease-out;
`;

const PackagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
  margin-top: 24px;
`;

const PackageCard = styled.button<{ $selected?: boolean; $isActive?: boolean; $isDark?: boolean }>`
  background: ${props => {
    if (props.$selected) {
      return props.$isDark
        ? 'linear-gradient(145deg, rgba(0, 200, 224, 0.15), rgba(0, 200, 224, 0.05))'
        : 'linear-gradient(145deg, rgba(0, 163, 184, 0.12), rgba(0, 163, 184, 0.04))';
    }
    return props.$isDark ? 'rgba(21, 28, 36, 0.6)' : 'rgba(241, 245, 249, 0.8)';
  }};
  border: 2px solid
    ${props => {
      if (props.$selected) {
        return props.$isDark ? '#00c8e0' : '#00a3b8';
      }
      return props.$isDark ? 'rgba(42, 53, 68, 0.8)' : 'rgba(226, 232, 240, 0.8)';
    }};
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.25s ease;
  position: relative;
  overflow: hidden;
  text-align: left;

  ${props =>
    props.$selected &&
    css`
      animation: ${pulseGlow} 2s ease-in-out infinite;
    `}

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent,
      ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.1)' : 'rgba(0, 163, 184, 0.08)')},
      transparent
    );
    background-size: 200% 100%;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    border-color: ${props =>
      props.$selected
        ? props.$isDark
          ? '#00c8e0'
          : '#00a3b8'
        : props.$isDark
          ? 'rgba(0, 200, 224, 0.5)'
          : 'rgba(0, 163, 184, 0.5)'};
    transform: translateY(-2px);

    &::before {
      opacity: 1;
      animation: ${shimmer} 1.5s infinite;
    }
  }

  &:focus {
    outline: none;
    border-color: ${props => (props.$isDark ? '#00c8e0' : '#00a3b8')};
  }
`;

const PackageName = styled.div<{ $isDark?: boolean }>`
  font-family: 'JetBrains Mono', monospace;
  font-size: 1rem;
  font-weight: 600;
  color: ${props => (props.$isDark ? '#f0f4f8' : '#171717')};
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActiveBadge = styled.span<{ $isDark?: boolean }>`
  font-size: 0.65rem;
  padding: 2px 6px;
  background: ${props => (props.$isDark ? 'rgba(0, 255, 127, 0.2)' : 'rgba(22, 163, 74, 0.15)')};
  border: 1px solid ${props => (props.$isDark ? 'rgba(0, 255, 127, 0.4)' : 'rgba(22, 163, 74, 0.4)')};
  border-radius: 4px;
  color: ${props => (props.$isDark ? '#00ff7f' : '#16a34a')};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const PackageTokens = styled.div<{ $isDark?: boolean }>`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => (props.$isDark ? '#00c8e0' : '#00a3b8')};
  margin-bottom: 4px;
  font-family: 'JetBrains Mono', monospace;
`;

const PackagePrice = styled.div<{ $isDark?: boolean }>`
  font-size: 0.875rem;
  color: ${props => (props.$isDark ? '#94a3b8' : '#525252')};
`;

const SelectedPackageDetails = styled.div<{ $isDark?: boolean }>`
  background: ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.05)' : 'rgba(0, 163, 184, 0.05)')};
  border: 1px solid ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.2)' : 'rgba(0, 163, 184, 0.2)')};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
`;

const DetailsHeader = styled.div<{ $isDark?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${props => (props.$isDark ? 'rgba(42, 53, 68, 0.6)' : 'rgba(226, 232, 240, 0.8)')};
`;

const DetailTitle = styled.h3<{ $isDark?: boolean }>`
  font-family: 'JetBrains Mono', monospace;
  font-size: 1.125rem;
  color: ${props => (props.$isDark ? '#f0f4f8' : '#171717')};
  margin: 0;
  font-weight: 600;
`;

const DetailPrice = styled.div<{ $isDark?: boolean }>`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${props => (props.$isDark ? '#00c8e0' : '#00a3b8')};
  font-family: 'JetBrains Mono', monospace;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 8px;
`;

const FeatureItem = styled.li<{ $isDark?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.875rem;
  color: ${props => (props.$isDark ? '#94a3b8' : '#525252')};

  svg {
    width: 16px;
    height: 16px;
    color: ${props => (props.$isDark ? '#00ff7f' : '#16a34a')};
    flex-shrink: 0;
  }
`;

// Order Preview styles
const OrderPreviewCard = styled.div<{ $isDark?: boolean }>`
  background: ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.05)' : 'rgba(0, 163, 184, 0.05)')};
  border: 1px solid ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.2)' : 'rgba(0, 163, 184, 0.2)')};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
`;

const OrderTitle = styled.div<{ $isDark?: boolean }>`
  font-family: 'JetBrains Mono', monospace;
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => (props.$isDark ? '#00c8e0' : '#00a3b8')};
  margin-bottom: 20px;
  text-align: center;
  padding-bottom: 16px;
  border-bottom: 1px solid ${props => (props.$isDark ? 'rgba(42, 53, 68, 0.6)' : 'rgba(226, 232, 240, 0.8)')};
`;

const OrderRow = styled.div<{ $isDark?: boolean; $isTotal?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: ${props =>
    props.$isTotal ? 'none' : `1px solid ${props.$isDark ? 'rgba(42, 53, 68, 0.4)' : 'rgba(226, 232, 240, 0.6)'}`};

  ${props =>
    props.$isTotal &&
    css`
      margin-top: 8px;
      padding-top: 16px;
      border-top: 2px solid ${props.$isDark ? 'rgba(0, 200, 224, 0.3)' : 'rgba(0, 163, 184, 0.3)'};
    `}
`;

const OrderLabel = styled.span<{ $isDark?: boolean; $isTotal?: boolean }>`
  font-family: 'JetBrains Mono', monospace;
  font-size: ${props => (props.$isTotal ? '1rem' : '0.875rem')};
  font-weight: ${props => (props.$isTotal ? '600' : '500')};
  color: ${props => (props.$isDark ? '#94a3b8' : '#525252')};
`;

const OrderValue = styled.span<{ $isDark?: boolean; $isTotal?: boolean }>`
  font-family: 'JetBrains Mono', monospace;
  font-size: ${props => (props.$isTotal ? '1.25rem' : '0.875rem')};
  font-weight: ${props => (props.$isTotal ? '700' : '500')};
  color: ${props => (props.$isTotal ? (props.$isDark ? '#00c8e0' : '#00a3b8') : props.$isDark ? '#f0f4f8' : '#171717')};
`;

const ModalFooter = styled.div<{ $isDark?: boolean }>`
  padding: 20px 32px 28px;
  border-top: 1px solid ${props => (props.$isDark ? 'rgba(42, 53, 68, 0.6)' : 'rgba(226, 232, 240, 0.8)')};
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary'; $isDark?: boolean }>`
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  ${props =>
    props.$variant === 'primary'
      ? css<{ $isDark?: boolean }>`
          background: linear-gradient(
            135deg,
            ${p => (p.$isDark ? '#00c8e0' : '#00a3b8')},
            ${p => (p.$isDark ? '#007d8f' : '#0e7490')}
          );
          border: none;
          color: ${p => (p.$isDark ? '#0a0e14' : '#ffffff')};

          &:hover:not(:disabled) {
            background: linear-gradient(
              135deg,
              ${p => (p.$isDark ? '#41e2f7' : '#22d3ee')},
              ${p => (p.$isDark ? '#00c8e0' : '#00a3b8')}
            );
            box-shadow: 0 0 20px ${p => (p.$isDark ? 'rgba(0, 200, 224, 0.4)' : 'rgba(0, 163, 184, 0.3)')};
            transform: translateY(-1px);
          }

          &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
        `
      : css<{ $isDark?: boolean }>`
          background: transparent;
          border: 1px solid ${p => (p.$isDark ? 'rgba(148, 163, 184, 0.3)' : 'rgba(148, 163, 184, 0.4)')};
          color: ${p => (p.$isDark ? '#94a3b8' : '#64748b')};

          &:hover {
            border-color: ${p => (p.$isDark ? '#94a3b8' : '#64748b')};
            color: ${p => (p.$isDark ? '#f0f4f8' : '#171717')};
          }
        `}
`;

const LoadingSpinner = styled.div<{ $isDark?: boolean }>`
  width: 18px;
  height: 18px;
  border: 2px solid ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.2)' : 'rgba(0, 163, 184, 0.2)')};
  border-top-color: ${props => (props.$isDark ? '#00c8e0' : '#00a3b8')};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
`;

const EmptyIcon = styled.div<{ $isDark?: boolean }>`
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  background: ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.1)' : 'rgba(0, 163, 184, 0.1)')};
  border: 1px solid ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.2)' : 'rgba(0, 163, 184, 0.2)')};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 32px;
    height: 32px;
    color: ${props => (props.$isDark ? '#00c8e0' : '#00a3b8')};
  }
`;

const EmptyTitle = styled.h3<{ $isDark?: boolean }>`
  font-family: 'JetBrains Mono', monospace;
  font-size: 1.125rem;
  color: ${props => (props.$isDark ? '#f0f4f8' : '#171717')};
  margin: 0 0 8px;
`;

const EmptyText = styled.p<{ $isDark?: boolean }>`
  font-size: 0.875rem;
  color: ${props => (props.$isDark ? '#94a3b8' : '#525252')};
  margin: 0 0 24px;
  max-width: 320px;
  margin-left: auto;
  margin-right: auto;
`;

const ContactButton = styled.a<{ $isDark?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: transparent;
  border: 1px solid ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.4)' : 'rgba(0, 163, 184, 0.4)')};
  border-radius: 8px;
  color: ${props => (props.$isDark ? '#00c8e0' : '#00a3b8')};
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.1)' : 'rgba(0, 163, 184, 0.1)')};
    border-color: ${props => (props.$isDark ? '#00c8e0' : '#00a3b8')};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

// Icons
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const CreditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v12M8 10h8M8 14h8" />
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M22 6l-10 7L2 6" />
  </svg>
);

// Component Props
interface AddCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: PackageConfig[];
  tokensAmount?: number;
  isDisabledAction: boolean;
  isLoading: boolean;
}

const AddCreditsModal: FC<AddCreditsModalProps> = ({
  isOpen,
  onClose,
  config,
  tokensAmount = 0,
  isDisabledAction,
  isLoading,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [step, setStep] = useState<1 | 2>(1);
  const { period, setPeriod } = usePricingContext();
  const prevIsOpenRef = useRef(false);

  // Filter out free packages
  const paidPackages = config.filter(pkg => !pkg.isFree);
  const selectedPackage = paidPackages.find(p => p.id === selectedPlanId);

  const packageFeatures =
    selectedPackage?.subtitle
      ?.split(';')
      .map(desc => desc.trim())
      .filter(desc => desc) || [];

  const isCurrentPackage = selectedPackage?.isActive;

  // Calculate pricing for order preview
  const getPrice = () => {
    if (!selectedPackage) return 0;
    // Extract price from packagePriceTitle (e.g., "$79.20/month")
    const priceMatch = selectedPackage.packagePriceTitle?.match(/\$?([\d,.]+)/);
    return priceMatch ? parseFloat(priceMatch[1].replace(',', '')) : 0;
  };

  const basePrice = getPrice();
  const processingFee = basePrice * PROCESSING_FEE;
  const totalPrice = basePrice + processingFee;

  // Auto-select active package or first package on open (only when modal opens)
  useEffect(() => {
    // Only initialize when modal transitions from closed to open
    if (isOpen && !prevIsOpenRef.current && paidPackages.length > 0) {
      const activePackage = paidPackages.find(p => p.isActive);
      setSelectedPlanId(activePackage?.id || paidPackages[0].id);
      setStep(1); // Reset to step 1 when opening
    }
    prevIsOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, config]);

  const handleContinueToPreview = () => {
    if (selectedPackage) {
      setStep(2);
    }
  };

  const handleBackToSelection = () => {
    setStep(1);
  };

  const handleConfirmPayment = async () => {
    if (selectedPackage) {
      // Wait for the checkout to complete before closing
      await selectedPackage.onClick();
      onClose();
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleClose = useCallback(() => {
    setStep(1);
    onClose();
  }, [onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  const step1ButtonText = selectedPackage
    ? isCurrentPackage
      ? t('billing.other.topUpText', 'Top up')
      : selectedPackage.packageButtonLocale
        ? t(selectedPackage.packageButtonLocale)
        : t('billing.other.changePlan', 'Change plan')
    : t('billing.other.selectPlan', 'Select a plan');

  // Use portal to render modal at document body level to avoid stacking context issues
  return createPortal(
    <ModalOverlay onClick={handleOverlayClick} $isDark={isDarkMode}>
      <ModalContainer
        data-tour="tour-credits-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        $isDark={isDarkMode}
      >
        <CloseButton onClick={handleClose} aria-label="Close modal" $isDark={isDarkMode}>
          <CloseIcon />
        </CloseButton>

        <ModalHeader $isDark={isDarkMode}>
          <div>
            <ModalTitle id="modal-title" $isDark={isDarkMode}>
              {step === 1 ? (
                <>
                  Add <span>Credits</span>
                </>
              ) : (
                <>
                  Order <span>Preview</span>
                </>
              )}
            </ModalTitle>
          </div>
          <CurrentBalance $isDark={isDarkMode}>
            <span>
              <CountUp isCounting key={tokensAmount} start={tokensAmount} end={tokensAmount} duration={0} />{' '}
              {t('common.tokens')}
            </span>
          </CurrentBalance>
        </ModalHeader>

        {/* Stepper */}
        <StepperContainer $isDark={isDarkMode}>
          <StepIndicator>
            <StepCircle $active={step === 1} $completed={step > 1} $isDark={isDarkMode}>
              {step > 1 ? <CheckIcon /> : '1'}
            </StepCircle>
            <StepLabel $active={step === 1} $isDark={isDarkMode}>
              {t('billing.steps.selectPlan', 'Select Plan')}
            </StepLabel>
          </StepIndicator>

          <StepConnector $completed={step > 1} $isDark={isDarkMode} />

          <StepIndicator>
            <StepCircle $active={step === 2} $isDark={isDarkMode}>
              2
            </StepCircle>
            <StepLabel $active={step === 2} $isDark={isDarkMode}>
              {t('billing.steps.confirm', 'Confirm')}
            </StepLabel>
          </StepIndicator>
        </StepperContainer>

        <ModalBody>
          {isLoading ? (
            <EmptyState>
              <LoadingSpinner style={{ margin: '0 auto 16px' }} $isDark={isDarkMode} />
              <EmptyText $isDark={isDarkMode}>{t('common.loading', 'Loading packages...')}</EmptyText>
            </EmptyState>
          ) : paidPackages.length > 0 ? (
            <>
              {step === 1 ? (
                <StepContent key="step1">
                  <Toggle active={period} setActive={setPeriod} isMobile={false} />
                  <PackagesGrid data-tour="tour-packages">
                    {paidPackages.map(pkg => (
                      <PackageCard
                        key={pkg.id}
                        type="button"
                        $selected={selectedPlanId === pkg.id}
                        $isActive={pkg.isActive}
                        $isDark={isDarkMode}
                        onClick={() => setSelectedPlanId(pkg.id)}
                      >
                        <PackageName $isDark={isDarkMode}>
                          {t(pkg.title)}
                          {pkg.isActive && <ActiveBadge $isDark={isDarkMode}>Active</ActiveBadge>}
                        </PackageName>
                        <PackageTokens $isDark={isDarkMode}>{pkg.tokensInPackage?.toLocaleString()}</PackageTokens>
                        <PackagePrice $isDark={isDarkMode}>{pkg.packagePriceTitle}</PackagePrice>
                      </PackageCard>
                    ))}
                  </PackagesGrid>

                  {selectedPackage && packageFeatures.length > 0 && (
                    <SelectedPackageDetails $isDark={isDarkMode}>
                      <DetailsHeader $isDark={isDarkMode}>
                        <DetailTitle $isDark={isDarkMode}>{t(selectedPackage.title)}</DetailTitle>
                        <DetailPrice $isDark={isDarkMode}>
                          {selectedPackage.tokensInPackage?.toLocaleString()} {t('billing.tokensPerMonth')}
                        </DetailPrice>
                      </DetailsHeader>
                      <FeaturesList>
                        {packageFeatures.map(feature => (
                          <FeatureItem key={feature} $isDark={isDarkMode}>
                            <CheckIcon />
                            {feature}
                          </FeatureItem>
                        ))}
                      </FeaturesList>
                    </SelectedPackageDetails>
                  )}
                </StepContent>
              ) : (
                <StepContent key="step2">
                  {selectedPackage && (
                    <OrderPreviewCard $isDark={isDarkMode}>
                      <OrderTitle $isDark={isDarkMode}>
                        {selectedPackage.tokensInPackage?.toLocaleString()} {t('billing.tokensPerMonth')}
                      </OrderTitle>

                      <OrderRow $isDark={isDarkMode}>
                        <OrderLabel $isDark={isDarkMode}>
                          {isCurrentPackage
                            ? t('billing.singleTopupPrice', 'Single top-up price')
                            : t('billing.subscriptionPrice', 'Subscription price')}
                        </OrderLabel>
                        <OrderValue $isDark={isDarkMode}>${basePrice.toFixed(2)}</OrderValue>
                      </OrderRow>

                      <OrderRow $isDark={isDarkMode}>
                        <OrderLabel $isDark={isDarkMode}>
                          {t('billing.processingFee', 'Processing fee')} (3%)
                        </OrderLabel>
                        <OrderValue $isDark={isDarkMode}>${processingFee.toFixed(2)}</OrderValue>
                      </OrderRow>

                      <OrderRow $isDark={isDarkMode} $isTotal>
                        <OrderLabel $isDark={isDarkMode} $isTotal>
                          {t('billing.totalPrice', 'Total price')}
                        </OrderLabel>
                        <OrderValue $isDark={isDarkMode} $isTotal>
                          ${totalPrice.toFixed(2)}
                        </OrderValue>
                      </OrderRow>
                    </OrderPreviewCard>
                  )}

                  {selectedPackage && packageFeatures.length > 0 && (
                    <SelectedPackageDetails $isDark={isDarkMode}>
                      <DetailsHeader $isDark={isDarkMode}>
                        <DetailTitle $isDark={isDarkMode}>{t(selectedPackage.title)}</DetailTitle>
                      </DetailsHeader>
                      <FeaturesList>
                        {packageFeatures.map(feature => (
                          <FeatureItem key={feature} $isDark={isDarkMode}>
                            <CheckIcon />
                            {feature}
                          </FeatureItem>
                        ))}
                      </FeaturesList>
                    </SelectedPackageDetails>
                  )}
                </StepContent>
              )}
            </>
          ) : (
            <EmptyState>
              <EmptyIcon $isDark={isDarkMode}>
                <CreditIcon />
              </EmptyIcon>
              <EmptyTitle $isDark={isDarkMode}>{t('billing.addCredits', 'Add Credits')}</EmptyTitle>
              <EmptyText $isDark={isDarkMode}>
                {t('billing.noPackagesConfigured', 'Credit packages are being set up. Contact support for assistance.')}
              </EmptyText>
              <ContactButton
                href="mailto:support@scrapeking.com?subject=Credit%20Package%20Request"
                $isDark={isDarkMode}
              >
                <MailIcon />
                {t('billing.contactSupport', 'Contact Support')}
              </ContactButton>
            </EmptyState>
          )}
        </ModalBody>

        {paidPackages.length > 0 && (
          <ModalFooter $isDark={isDarkMode}>
            {step === 1 ? (
              <>
                <Button $variant="secondary" onClick={handleClose} $isDark={isDarkMode}>
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button
                  $variant="primary"
                  onClick={handleContinueToPreview}
                  disabled={isDisabledAction || !selectedPackage}
                  $isDark={isDarkMode}
                >
                  {step1ButtonText}
                </Button>
              </>
            ) : (
              <>
                <Button $variant="secondary" onClick={handleBackToSelection} $isDark={isDarkMode}>
                  {t('common.back', 'Back')}
                </Button>
                <Button
                  $variant="primary"
                  onClick={handleConfirmPayment}
                  disabled={isDisabledAction || !selectedPackage}
                  $isDark={isDarkMode}
                >
                  {t('common.confirm', 'Confirm')} - ${totalPrice.toFixed(2)}
                </Button>
              </>
            )}
          </ModalFooter>
        )}
      </ModalContainer>
    </ModalOverlay>,
    document.body,
  );
};

export default memo(AddCreditsModal);
